import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import {
  getSavedTimezone,
  setSavedTimezone,
} from 'ui_system/utilities/timezoneUtils';
import TimezoneModal from './TimezoneModal';

const timeFormat = 'h:mma';
const dateFormat = 'YYYY-MM-DD';

const TimezoneWrapper = ({
  children,
  enabled,
  tourTimezone,
  availableDatetimes,
  dateValue,
  timeValue,
  setDateTime,
  clearTime,
  setGuestTimezone,
}) => {
  const initialTimezone = getSavedTimezone() || tourTimezone;

  const [timezone, setTimezone] = useState(initialTimezone);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentDateValue, setCurrentDateValue] = useState(dateValue);

  // availableDates holds the converted dates hash in the Guest Timezone
  // for feeding the Dates and Times child components
  // Computed unconditionally to allow hooks to reference it
  const availableDates = {};
  availableDatetimes.forEach(({ datetime, spots_left: spotsLeft }) => {
    const convertedDatetime = moment(datetime).tz(timezone);
    const date = convertedDatetime.format(dateFormat);
    const time = convertedDatetime.format(timeFormat);

    if (!availableDates[date]) availableDates[date] = {};
    availableDates[date][time] = spotsLeft;
  });

  // Effect for disabled mode - must be called unconditionally
  useEffect(() => {
    if (!enabled) {
      setGuestTimezone({ timezone: moment.tz.guess() });
    }
  }, [enabled, setGuestTimezone]);

  // Select the first valid date if timezone changes
  // Must be called unconditionally to comply with rules-of-hooks
  useEffect(() => {
    if (!enabled) return;

    const availableDateKeys = Object.keys(availableDates);
    const currentDateValid =
      currentDateValue && availableDates && availableDates[currentDateValue];
    if (!currentDateValid && availableDateKeys.length > 0) {
      setCurrentDateValue(availableDateKeys[0]);
    }

    // Persist timezone to formstore
    setGuestTimezone({ timezone });
  }, [
    enabled,
    timezone,
    currentDateValue,
    setGuestTimezone,
    availableDatetimes,
  ]);

  // Early return must come after all hooks to comply with rules-of-hooks
  if (!enabled) {
    return children;
  }

  // availableTimes holds the available times for the current date selected, in the Guest Timezone
  const availableTimes = availableDates[currentDateValue] || {};

  const onSelectTimezone = (selectedTimezone) => {
    setSavedTimezone(selectedTimezone);
    setModalOpen(false);
    setTimezone(selectedTimezone);
    setGuestTimezone(selectedTimezone);
  };

  const onChangeDate = (_name, value) => {
    setCurrentDateValue(value);
  };

  // If valid: Convert selected time value into Tour Timezone for formstore
  // If null: clear the time selection in the formstore
  const onChangeTime = (_name, value) => {
    if (!value) {
      clearTime();
      return;
    }

    const selectedDateTime = moment
      .tz(
        currentDateValue + value.trim(),
        `${dateFormat} ${timeFormat}`,
        timezone
      )
      .tz(tourTimezone);

    const date = selectedDateTime.format(dateFormat);
    const time = selectedDateTime.format(timeFormat);
    setDateTime({ date, time });
  };

  const dateProps = () => {
    return { availableDates, onChange: onChangeDate, value: currentDateValue };
  };

  const timeProps = () => {
    // Convert the stored time value in the formstore back to Guest Timezone
    let selectedTime;
    if (timeValue) {
      selectedTime = moment
        .tz(
          currentDateValue + timeValue.trim(),
          `${dateFormat} ${timeFormat}`,
          tourTimezone
        )
        .tz(timezone)
        .format(timeFormat);
    }

    return {
      times: availableTimes,
      onChange: onChangeTime,
      value: selectedTime,
      date: currentDateValue,
      dateSelected: !!currentDateValue,
    };
  };

  const childComponents = React.Children.map(children, (child) => {
    if (!child) return null;

    const componentName = child.type.displayName;
    let childProps = {
      timezone,
      timezonesEnabled: enabled,
      openTimezoneModal: setModalOpen,
    };

    if (componentName === 'BookingRequest-Dates')
      childProps = { ...childProps, ...dateProps() };
    if (componentName === 'BookingRequest-Times')
      childProps = { ...childProps, ...timeProps() };
    return React.cloneElement(child, childProps);
  });

  return (
    <React.Fragment>
      {childComponents}
      <TimezoneModal
        open={modalOpen}
        close={() => setModalOpen(false)}
        timezone={timezone}
        setTimezone={onSelectTimezone}
      />
    </React.Fragment>
  );
};

TimezoneWrapper.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  enabled: PropTypes.bool.isRequired,
  setGuestTimezone: PropTypes.func.isRequired,
  tourTimezone: PropTypes.string.isRequired,
  availableDatetimes: PropTypes.array.isRequired,
  dateValue: PropTypes.string,
  timeValue: PropTypes.string,
  setDateTime: PropTypes.func.isRequired,
  clearTime: PropTypes.func.isRequired,
};

export default TimezoneWrapper;
